import { useEffect, useState, useRef } from "react";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import ThemeTabs from "components/Tabs/ThemeTabs";
import { app_modules } from "helpers/apps_modules_list";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import StockOverView from "./Listing/StockOverView/StockOverView";
import WareHouseWiseStock from "./Listing/WareHouseWiseStock/WareHouseWiseStock";
import { exportInstockCSV } from "slices/Inventory/Inventory/Stock_Overview/thunk";
import NoWarehousesMessage from "Common/Components/NoWarehousesMessage";

const InventoryInStock = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const prevTabRef = useRef<number | null>(null);

  const [tab, setTab] = useState(
    Number(sessionStorage.getItem("Instocktab")) || 1
  );

  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔥 Use preserved table state hook with tabIndex for per-tab state preservation
  const tableState = usePreservedTableState({
    key: "inventory",
    tabIndex: tab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection: "" | "asc" | "desc" | undefined = sorting[0]?.desc
    ? "desc"
    : sorting[0]
    ? "asc"
    : "";

  // 🔥 Warehouse search (optional) - stored separately as it's not part of table state
  const [warehouseIdSearch, setWarehouseIdSearch] = useState("");

  // Use custom hook to fetch and access permissions
  const { canView } = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.in_stock
  );

  // const warehouse_permissions = user_permissions?.find(
  //   (item: any) => item?.apps_name === app_modules?.warehouse?.app_name
  // );
  // const item_inward_permissions =
  //   warehouse_permissions?.modules_permissions?.find(
  //     (item: any) =>
  //       item?.modules_name === app_modules?.warehouse?.modules?.item_inward
  //   );

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // ✅ Tab switch - reset state when tab changes
  const handleTabChange = (val: number) => {
    setTab(val);
    sessionStorage.setItem("Instocktab", val.toString());
  };

  // Reset state when tab changes (using useEffect to handle tab changes)
  // This ensures state is cleared when switching tabs, but preserved when navigating away and back
  useEffect(() => {
    // Only reset if tab actually changed (not on initial mount)
    // The hook will have re-initialized with the new tabIndex by this point
    if (prevTabRef.current !== null && prevTabRef.current !== tab) {
      // Clear the state for the new tab
      resetState();
    }
    prevTabRef.current = tab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]); // Only depend on tab, not resetState to avoid infinite loops

  const tabs = [{ label: "Stock Overview" }, { label: "Warehouse-wise Stock" }];

  // EXPORT BUTTON
  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const payload = {
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
        warehouse_id: tab === 2 ? warehouseIdSearch : null,
      };

      await exportInstockCSV({ ...payload });

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export order data";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  // INIT FETCH

  useEffect(() => {
    const fetchInit = async () => {
      try {
        setLoading(true);

        const { payload } = await dispatch(fetchWarehouseDropdown());
        setWarehouseOptions(payload.data || []);

        // Check if stored warehouse is still valid (exists in current options)
        const storedWarehouse = sessionStorage.getItem("WarehouseIdInventory");
        const isStoredWarehouseValid = storedWarehouse &&
          payload.data?.some((warehouse: any) => warehouse.warehouse_id === storedWarehouse);

        if (storedWarehouse && isStoredWarehouseValid) {
          setWarehouseIdSearch(storedWarehouse);
        } else {
          // Clear invalid stored warehouse and auto-select first available
          sessionStorage.removeItem("WarehouseIdInventory");
          setWarehouseIdSearch(payload.data?.[0]?.warehouse_id || "");
          sessionStorage.setItem(
            "WarehouseIdInventory",
            payload.data?.[0]?.warehouse_id || ""
          );
        }
      } catch (error) {
        toast.error("Failed to fetch initial data");
      } finally {
        setLoading(false);
      }
    };

    fetchInit();
  }, [tab, dispatch]); //

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  return (
    <ListingPageContainer>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col xs={12} sm={12} md={12} lg={2} xl={2}>
                  <h6 className="card-title mb-0">Inventory</h6>
                </Col>

                {/* Right-aligned elements container */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={10}
                  xl={10}
                  className="mb-2 mb-lg-0"
                >
                  <Row className="g-2 justify-content-end align-items-center">
                    {/* Search */}
                    <Col xs={12} sm={12} md={6} lg={3} xl={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    {/* Warehouse Dropdown — ONLY for Tab 2 */}
                    {tab === 2 && (
                      <Col
                        xs={12}
                        sm={12}
                        md={6}
                        lg={4}
                        xl={4}
                        style={{ minWidth: "200px" }}
                      >
                        <WarehouseAutocomplete
                          options={warehouseOptions}
                          value={warehouseIdSearch}
                          loading={loading}
                          showWarehouseType={true}
                          disableClearable
                          sessionStorageKey="WarehouseIdInventory"
                          onChange={(warehouseId) => {
                            setWarehouseIdSearch(String(warehouseId));

                            // Reset filters and sorting when warehouse changes
                            setColumnFilters([]);
                            setFilterValue([]);
                            setSorting([]);
                            setPageIndex(0);
                          }}
                        />
                      </Col>
                    )}

                    {/* Buttons */}
                    <Col lg="auto" className="d-flex gap-2">
                      {canView && (
                        <ActionButton
                          variant="light"
                          label="Export"
                          icon="bi bi-box-arrow-up"
                          handleClick={handleExport}
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>

              {/* Tabs */}
              <Row className="mt-3">
                <Col xs={12}>
                  <ThemeTabs
                    tabs={tabs}
                    value={tab - 1}
                    onChange={(v) => handleTabChange(v + 1)}
                  />
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              {/* {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <> */}
              {/* TAB 1 — STOCK OVERVIEW */}
              {tab === 1 && (
                <StockOverView
                  loading={loading}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  sorting={sorting}
                  columnFilters={columnFilters}
                  filterValue={filterValue}
                  search={search}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  setPageIndex={setPageIndex}
                  setPageSize={setPageSize}
                  setSorting={setSorting}
                  setColumnFilters={setColumnFilters}
                  setFilterValue={setFilterValue}
                />
              )}

              {/* TAB 2 — WAREHOUSE-WISE */}
              {tab === 2 && (
                <>
                  {warehouseOptions.length > 0 ? (
                    <WareHouseWiseStock
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  ) : (
                    <NoWarehousesMessage />
                  )}
                </>
              )}
              {/* </>
              )} */}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default InventoryInStock;
